import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";
import { useAuth } from "./auth";

/**
 * Wishlist context tracks BOTH saved Ads (by listing id) and saved Digital
 * Stores (by shop slug) for the current user. One context, two sets — so
 * tiles can render the heart state instantly without a per-card API call.
 */
const WishlistContext = createContext(null);

export function WishlistProvider({ children }) {
    const { user } = useAuth();
    const [ids, setIds] = useState(new Set());            // listing ids
    const [shopSlugs, setShopSlugs] = useState(new Set()); // shop slugs
    const [ready, setReady] = useState(false);

    const load = useCallback(async () => {
        if (!user) {
            setIds(new Set());
            setShopSlugs(new Set());
            setReady(true);
            return;
        }
        try {
            const [adsRes, shopsRes] = await Promise.all([
                api.get("/wishlist/ids").catch(() => ({ data: { ids: [] } })),
                api.get("/wishlist/shops/ids").catch(() => ({ data: { shop_slugs: [] } })),
            ]);
            setIds(new Set(adsRes.data.ids || []));
            setShopSlugs(new Set(shopsRes.data.shop_slugs || []));
        } catch {
            setIds(new Set());
            setShopSlugs(new Set());
        } finally {
            setReady(true);
        }
    }, [user]);

    useEffect(() => { setReady(false); load(); }, [load]);

    const toggle = useCallback(async (listingId) => {
        // optimistic — toggle the local set first, then sync to server.
        setIds((prev) => {
            const next = new Set(prev);
            if (next.has(listingId)) next.delete(listingId); else next.add(listingId);
            return next;
        });
        try {
            const { data } = await api.post("/wishlist/toggle", { listing_id: listingId });
            setIds((prev) => {
                const next = new Set(prev);
                if (data.wishlisted) next.add(listingId); else next.delete(listingId);
                return next;
            });
            return data.wishlisted;
        } catch (e) {
            setIds((prev) => {
                const next = new Set(prev);
                if (next.has(listingId)) next.delete(listingId); else next.add(listingId);
                return next;
            });
            throw e;
        }
    }, []);

    /** Toggle a Digital Store's saved state. Same optimistic pattern as
     *  `toggle` above but writes to `db.wishlisted_shops` via the
     *  /wishlist/shops/toggle endpoint. */
    const toggleShop = useCallback(async (shopSlug) => {
        setShopSlugs((prev) => {
            const next = new Set(prev);
            if (next.has(shopSlug)) next.delete(shopSlug); else next.add(shopSlug);
            return next;
        });
        try {
            const { data } = await api.post("/wishlist/shops/toggle", { shop_slug: shopSlug });
            setShopSlugs((prev) => {
                const next = new Set(prev);
                if (data.wishlisted) next.add(shopSlug); else next.delete(shopSlug);
                return next;
            });
            return data.wishlisted;
        } catch (e) {
            setShopSlugs((prev) => {
                const next = new Set(prev);
                if (next.has(shopSlug)) next.delete(shopSlug); else next.add(shopSlug);
                return next;
            });
            throw e;
        }
    }, []);

    const isWishlisted = useCallback((listingId) => ids.has(listingId), [ids]);
    const isShopWishlisted = useCallback((shopSlug) => shopSlugs.has(shopSlug), [shopSlugs]);

    return (
        <WishlistContext.Provider value={{
            ids, shopSlugs,
            isWishlisted, isShopWishlisted,
            toggle, toggleShop,
            reload: load, ready,
        }}>
            {children}
        </WishlistContext.Provider>
    );
}

export const useWishlist = () => useContext(WishlistContext) || {
    ids: new Set(), shopSlugs: new Set(),
    isWishlisted: () => false, isShopWishlisted: () => false,
    toggle: async () => { }, toggleShop: async () => { },
    reload: () => { }, ready: false,
};
